// =============================================================================
// src/features/analytics-live-hydrate.ts — Hidrata o Analytics com o
// social_listening (Hasura, source sl_*). Espelho de sentinel-live-hydrate:
// memoizado, roda no bootstrap ANTES dos mounts (render-time getters já leem o
// patch, incl. o re-render de granularidade do chart-controller) e NUNCA
// derruba o app — qualquer falha vira fallback estático silencioso.
// 2026-09-02: memoização por RECORTE (escopo × período × reações) — trocar um
// chip re-hidrata com o where novo no servidor (rehydrateAnalyticsLive) e
// SUBSTITUI o override (replaceAnalyticsLivePatch) em vez de mesclar.
// =============================================================================

import type { AnalyticsLiveClient } from '../data/analytics-client';
import type { AnalyticsIngestionGraphqlData } from '../data/analytics-live-query';
import { buildAnalyticsLivePatch } from '../data/analytics-overlay';
import { debugLiveIntegration } from '../data/live-debug';
import { replaceAnalyticsLivePatch } from './analytics/analytics-live-store';
import {
  analyticsViewStateKey,
  getAnalyticsViewState,
  type AnalyticsViewState,
} from './analytics/analytics-view-state';

export type AnalyticsLiveHydrationSource = 'live' | 'static';

export interface AnalyticsLiveStatus {
  readonly source: AnalyticsLiveHydrationSource;
  /** 'fetch_failed' quando o Hasura não respondeu (≠ coleta legitimamente vazia). */
  readonly reason?: 'client_null' | 'fetch_failed' | 'empty_dashboard';
}

let analyticsHydrationClient: AnalyticsLiveClient | null = null;
let analyticsIngestionOnce: Promise<AnalyticsIngestionGraphqlData> | null = null;
const analyticsHydrationByView = new Map<string, Promise<AnalyticsLiveStatus>>();

/** Hidratação memoizada por recorte; client null (env sem Hasura) → estático imediato. */
export function hydrateAnalyticsLive(client: AnalyticsLiveClient | null): Promise<AnalyticsLiveStatus> {
  analyticsHydrationClient = client;
  return hydrateAnalyticsView(getAnalyticsViewState());
}

/** Re-hidrata para o recorte ATUAL (chip/período trocado) com o client lembrado. */
export function rehydrateAnalyticsLive(): Promise<AnalyticsLiveStatus> {
  return hydrateAnalyticsView(getAnalyticsViewState());
}

function hydrateAnalyticsView(viewState: AnalyticsViewState): Promise<AnalyticsLiveStatus> {
  const viewKey = analyticsViewStateKey(viewState);
  const cached = analyticsHydrationByView.get(viewKey);
  if (cached) return cached.then((status) => reapplyIfLive(status, viewKey));
  const hydration = runAnalyticsHydration(analyticsHydrationClient, viewState);
  analyticsHydrationByView.set(viewKey, hydration);
  return hydration;
}

/** Recorte já buscado antes: só re-aplica o patch memoizado (sem novo fetch). */
async function reapplyIfLive(status: AnalyticsLiveStatus, viewKey: string): Promise<AnalyticsLiveStatus> {
  const patch = analyticsPatchByView.get(viewKey);
  if (patch) replaceAnalyticsLivePatch(patch);
  return status;
}

const analyticsPatchByView = new Map<string, ReturnType<typeof buildAnalyticsLivePatch>>();

async function runAnalyticsHydration(
  client: AnalyticsLiveClient | null,
  viewState: AnalyticsViewState,
): Promise<AnalyticsLiveStatus> {
  if (!client) {
    debugLiveIntegration('analytics-live', 'fallback_static', { reason: 'client_null' });
    return { source: 'static', reason: 'client_null' };
  }
  try {
    // Config de ingestão (021) viaja junto do dashboard: mesmo Hasura, mesma
    // janela de erro — falha de QUALQUER um derruba tudo para o estático (evita
    // meio-live com aba Configurações real e feed mock, que confundiria mais).
    // A config não depende do recorte: 1 fetch por client (memoizado).
    const [dashboard, ingestion] = await Promise.all([
      client.fetchAnalyticsDashboard(viewState),
      fetchAnalyticsIngestionOnce(client),
    ]);
    const patch = buildAnalyticsLivePatch(dashboard, ingestion);
    analyticsPatchByView.set(analyticsViewStateKey(viewState), patch);
    replaceAnalyticsLivePatch(patch);
    const patchedFields = Object.keys(patch).length;
    debugLiveIntegration('analytics-live', patchedFields > 0 ? 'live_dashboard' : 'fallback_static', {
      patchedFields,
      view: analyticsViewStateKey(viewState),
      dailyRows: dashboard.mentionsDaily.length,
      feed: patch.feedItems?.length ?? 0,
      authors: dashboard.authorWindow.length,
      newsSources: dashboard.newsSourceDaily.length,
      ingestionThemes: ingestion.watchQueries.length,
    });
    return patchedFields > 0 ? { source: 'live' } : { source: 'static', reason: 'empty_dashboard' };
  } catch (error) {
    debugLiveIntegration('analytics-live', 'fallback_static', {
      reason: 'fetch_analytics_dashboard_failed',
      error: error instanceof Error ? error.message : String(error),
    });
    return { source: 'static', reason: 'fetch_failed' };
  }
}

function fetchAnalyticsIngestionOnce(client: AnalyticsLiveClient): Promise<AnalyticsIngestionGraphqlData> {
  if (!analyticsIngestionOnce) {
    analyticsIngestionOnce = client.fetchIngestionConfig().catch((error: unknown) => {
      analyticsIngestionOnce = null; // falha não fica memoizada: o próximo recorte tenta de novo
      throw error;
    });
  }
  return analyticsIngestionOnce;
}

export function __resetAnalyticsLiveHydrationForTests(): void {
  analyticsHydrationClient = null;
  analyticsIngestionOnce = null;
  analyticsHydrationByView.clear();
  analyticsPatchByView.clear();
}
